'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Notifica {
  id: string;
  allarme_id: string | null;
  canale: string;
  stato: string;
  destinatario: string;
  oggetto: string;
  corpo: string;
  errore: string;
  letta_at: string | null;
  inviata_at: string | null;
  created_at: string;
  allarmi?: { titolo: string; priorita: string } | null;
}

interface RegolaNotifica {
  id: string;
  nome: string;
  attiva: boolean;
  tipo_allarme: string;
  priorita_minima: string;
  canali: string[];
  destinatari_email: string[];
  digest_frequenza: string;
  escalation_ore: number | null;
  created_at: string;
}

interface ImpostazioniNotifiche {
  id?: string;
  digest_frequenza: string;
  canali_abilitati: string[];
  email_notifiche: string;
  telefono_sms: string;
  notifiche_critiche_immediate: boolean;
  notifiche_alta_priorita: boolean;
  notifiche_media_priorita: boolean;
  notifiche_bassa_priorita: boolean;
}

const CANALE_ICONS: Record<string, string> = {
  email: 'EnvelopeIcon',
  in_app: 'BellIcon',
  sms: 'DevicePhoneMobileIcon',
  webhook: 'LinkIcon',
};

const STATO_NOTIFICA: Record<string, { label: string; bg: string; text: string }> = {
  in_attesa: { label: 'In Attesa', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  inviata: { label: 'Inviata', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  consegnata: { label: 'Consegnata', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  fallita: { label: 'Fallita', bg: 'bg-red-500/20', text: 'text-red-400' },
  letta: { label: 'Letta', bg: 'bg-gray-500/20', text: 'text-gray-400' },
};

const TIPO_ALLARME_LABELS: Record<string, string> = {
  dispositivo_offline: 'Dispositivo Offline',
  conflitto_ip: 'Conflitto IP',
  scadenza_manutenzione: 'Scadenza Manutenzione',
  scadenza_garanzia: 'Scadenza Garanzia',
  scadenza_licenza: 'Scadenza Licenza',
  scadenza_contratto: 'Scadenza Contratto',
  nuovo_dispositivo_non_censito: 'Nuovo Dispositivo',
  firmware_obsoleto: 'Firmware Obsoleto',
  vulnerabilita_cve: 'Vulnerabilità CVE',
  banda_eccessiva: 'Banda Eccessiva',
  temperatura_fuori_soglia: 'Temperatura Anomala',
  escalation_ticket: 'Escalation Ticket',
  altro: 'Altro',
};

const DEFAULT_IMPOSTAZIONI: ImpostazioniNotifiche = {
  digest_frequenza: 'immediata',
  canali_abilitati: ['in_app', 'email'],
  email_notifiche: '',
  telefono_sms: '',
  notifiche_critiche_immediate: true,
  notifiche_alta_priorita: true,
  notifiche_media_priorita: true,
  notifiche_bassa_priorita: false,
};

export default function NotificheContent() {
  const [activeTab, setActiveTab] = useState<'notifiche' | 'regole' | 'impostazioni'>('notifiche');
  const [notifiche, setNotifiche] = useState<Notifica[]>([]);
  const [regole, setRegole] = useState<RegolaNotifica[]>([]);
  const [impostazioni, setImpostazioni] = useState<ImpostazioniNotifiche>(DEFAULT_IMPOSTAZIONI);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegolaForm, setShowRegolaForm] = useState(false);
  const [newRegola, setNewRegola] = useState({
    nome: '', tipo_allarme: 'dispositivo_offline', priorita_minima: 'critico',
    canali: ['in_app', 'email'], destinatari_email: '', digest_frequenza: 'immediata', escalation_ore: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Utente non autenticato');

      const [notRes, regRes, impRes] = await Promise.all([
        supabase.from('notifiche').select('*, allarmi(titolo, priorita)')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }).limit(50),
        supabase.from('regole_notifica').select('*').order('created_at', { ascending: false }),
        supabase.from('impostazioni_notifiche').select('*').eq('user_id', user.id).limit(1).maybeSingle(),
      ]);
      if (notRes.error) throw notRes.error;
      if (regRes.error) throw regRes.error;
      setNotifiche((notRes.data as Notifica[]) || []);
      setRegole((regRes.data as RegolaNotifica[]) || []);
      if (impRes.data) setImpostazioni(impRes.data as ImpostazioniNotifiche);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveImpostazioni = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Utente non autenticato');
      const payload = { ...impostazioni, user_id: user.id };
      if (impostazioni.id) {
        await supabase.from('impostazioni_notifiche').update(payload).eq('id', impostazioni.id);
      } else {
        const { data } = await supabase.from('impostazioni_notifiche').insert(payload).select().single();
        if (data) setImpostazioni(data as ImpostazioniNotifiche);
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleRegola = async (id: string, attiva: boolean) => {
    await supabase.from('regole_notifica').update({ attiva: !attiva }).eq('id', id);
    await fetchData();
  };

  const deleteRegola = async (id: string) => {
    if (!confirm('Eliminare questa regola?')) return;
    await supabase.from('regole_notifica').delete().eq('id', id);
    await fetchData();
  };

  const saveRegola = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('regole_notifica').insert({
        nome: newRegola.nome,
        tipo_allarme: newRegola.tipo_allarme,
        priorita_minima: newRegola.priorita_minima,
        canali: newRegola.canali,
        destinatari_email: newRegola.destinatari_email.split(',').map(e => e.trim()).filter(Boolean),
        digest_frequenza: newRegola.digest_frequenza,
        escalation_ore: newRegola.escalation_ore ? parseInt(newRegola.escalation_ore) : null,
        created_by: user?.id,
      });
      setShowRegolaForm(false);
      setNewRegola({ nome: '', tipo_allarme: 'dispositivo_offline', priorita_minima: 'critico', canali: ['in_app', 'email'], destinatari_email: '', digest_frequenza: 'immediata', escalation_ore: '' });
      await fetchData();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const markAsRead = async (id: string) => {
    await supabase.from('notifiche').update({ stato: 'letta', letta_at: new Date().toISOString() }).eq('id', id);
    await fetchData();
  };

  const formatDate = (d: string) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const unreadCount = notifiche.filter(n => n.stato !== 'letta' && n.canale === 'in_app').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Icon name="BellIcon" size={28} className="text-sky-400" />
            Notifiche e Regole
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </h1>
          <p className="text-muted-color text-sm mt-1">Gestione notifiche, regole e impostazioni digest</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface rounded-xl p-1 border border-border w-fit">
        {[
          { v: 'notifiche', l: 'Notifiche', icon: 'BellIcon' },
          { v: 'regole', l: 'Regole', icon: 'AdjustmentsHorizontalIcon' },
          { v: 'impostazioni', l: 'Impostazioni', icon: 'Cog6ToothIcon' },
        ].map(({ v, l, icon }) => (
          <button key={v} onClick={() => setActiveTab(v as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === v ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white'}`}>
            <Icon name={icon as any} size={16} />
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">{error}</div>
      ) : (
        <>
          {/* Notifiche Tab */}
          {activeTab === 'notifiche' && (
            <div className="space-y-3">
              {notifiche.length === 0 ? (
                <div className="text-center py-12 text-muted-color">
                  <Icon name="BellSlashIcon" size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Nessuna notifica</p>
                </div>
              ) : notifiche.map(n => {
                const s = STATO_NOTIFICA[n.stato] || STATO_NOTIFICA.in_attesa;
                return (
                  <div key={n.id} className={`bg-surface border rounded-xl p-4 transition-all ${n.stato !== 'letta' && n.canale === 'in_app' ? 'border-sky-500/30' : 'border-border'}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center flex-shrink-0">
                        <Icon name={CANALE_ICONS[n.canale] as any || 'BellIcon'} size={18} className="text-sky-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-white text-sm truncate">{n.oggetto}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-surface-elevated text-muted-color capitalize">{n.canale}</span>
                        </div>
                        <p className="text-xs text-muted-color mt-1 line-clamp-2">{n.corpo}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-color">
                          <span>{n.destinatario}</span>
                          <span>{formatDate(n.created_at)}</span>
                        </div>
                      </div>
                      {n.stato !== 'letta' && n.canale === 'in_app' && (
                        <button onClick={() => markAsRead(n.id)}
                          className="px-3 py-1.5 bg-sky-500/20 text-sky-400 rounded-lg text-xs font-semibold hover:bg-sky-500/30 transition-colors flex-shrink-0">
                          Segna letta
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Regole Tab */}
          {activeTab === 'regole' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => setShowRegolaForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-xl font-semibold text-sm hover:bg-sky-600 transition-colors">
                  <Icon name="PlusIcon" size={16} />
                  Nuova Regola
                </button>
              </div>

              {showRegolaForm && (
                <div className="bg-surface border border-sky-500/30 rounded-2xl p-6 space-y-4">
                  <h3 className="font-semibold text-white">Nuova Regola di Notifica</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Nome Regola</label>
                      <input value={newRegola.nome} onChange={e => setNewRegola(p => ({ ...p, nome: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                        placeholder="Es. Alert Dispositivo Offline" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Tipo Allarme</label>
                      <select value={newRegola.tipo_allarme} onChange={e => setNewRegola(p => ({ ...p, tipo_allarme: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                        {Object.entries(TIPO_ALLARME_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Priorità Minima</label>
                      <select value={newRegola.priorita_minima} onChange={e => setNewRegola(p => ({ ...p, priorita_minima: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                        <option value="critico">Critico</option>
                        <option value="alto">Alto</option>
                        <option value="medio">Medio</option>
                        <option value="basso">Basso</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Frequenza Digest</label>
                      <select value={newRegola.digest_frequenza} onChange={e => setNewRegola(p => ({ ...p, digest_frequenza: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                        <option value="immediata">Immediata</option>
                        <option value="oraria">Oraria</option>
                        <option value="giornaliera">Giornaliera</option>
                        <option value="settimanale">Settimanale</option>
                        <option value="mai">Mai</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Email Destinatari (separati da virgola)</label>
                      <input value={newRegola.destinatari_email} onChange={e => setNewRegola(p => ({ ...p, destinatari_email: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                        placeholder="admin@ospedale.it, rete@ospedale.it" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-color mb-1 block">Escalation dopo (ore, opzionale)</label>
                      <input type="number" value={newRegola.escalation_ore} onChange={e => setNewRegola(p => ({ ...p, escalation_ore: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                        placeholder="Es. 2" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-color mb-2 block">Canali</label>
                    <div className="flex gap-3 flex-wrap">
                      {['email', 'in_app', 'sms', 'webhook'].map(c => (
                        <label key={c} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={newRegola.canali.includes(c)}
                            onChange={e => setNewRegola(p => ({ ...p, canali: e.target.checked ? [...p.canali, c] : p.canali.filter(x => x !== c) }))}
                            className="rounded" />
                          <span className="text-sm text-white capitalize">{c}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={saveRegola} disabled={saving || !newRegola.nome}
                      className="px-4 py-2 bg-sky-500 text-white rounded-xl font-semibold text-sm hover:bg-sky-600 transition-colors disabled:opacity-50">
                      {saving ? 'Salvataggio...' : 'Salva Regola'}
                    </button>
                    <button onClick={() => setShowRegolaForm(false)}
                      className="px-4 py-2 bg-surface-elevated text-muted-color rounded-xl font-semibold text-sm hover:text-white transition-colors">
                      Annulla
                    </button>
                  </div>
                </div>
              )}

              {regole.map(r => (
                <div key={r.id} className="bg-surface border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{r.nome}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.attiva ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                          {r.attiva ? 'Attiva' : 'Disattiva'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-color">
                        <span>{TIPO_ALLARME_LABELS[r.tipo_allarme] || r.tipo_allarme}</span>
                        <span>•</span>
                        <span>Priorità min: {r.priorita_minima}</span>
                        <span>•</span>
                        <span>Digest: {r.digest_frequenza}</span>
                        {r.escalation_ore && <><span>•</span><span>Escalation: {r.escalation_ore}h</span></>}
                      </div>
                      <div className="flex gap-1 mt-2">
                        {r.canali?.map(c => (
                          <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 capitalize">{c}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => toggleRegola(r.id, r.attiva)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${r.attiva ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'}`}>
                        {r.attiva ? 'Disattiva' : 'Attiva'}
                      </button>
                      <button onClick={() => deleteRegola(r.id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Impostazioni Tab */}
          {activeTab === 'impostazioni' && (
            <div className="max-w-2xl space-y-6">
              <div className="bg-surface border border-border rounded-2xl p-6 space-y-4">
                <h3 className="font-semibold text-white">Preferenze Personali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-color mb-1 block">Frequenza Digest</label>
                    <select value={impostazioni.digest_frequenza}
                      onChange={e => setImpostazioni(p => ({ ...p, digest_frequenza: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                      <option value="immediata">Immediata</option>
                      <option value="oraria">Oraria (digest)</option>
                      <option value="giornaliera">Giornaliera (digest)</option>
                      <option value="settimanale">Settimanale</option>
                      <option value="mai">Mai</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-color mb-1 block">Email Notifiche</label>
                    <input value={impostazioni.email_notifiche}
                      onChange={e => setImpostazioni(p => ({ ...p, email_notifiche: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                      placeholder="tua@email.it" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-color mb-1 block">Telefono SMS</label>
                    <input value={impostazioni.telefono_sms}
                      onChange={e => setImpostazioni(p => ({ ...p, telefono_sms: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                      placeholder="+39 333 1234567" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-color mb-2 block">Canali Abilitati</label>
                  <div className="flex gap-4 flex-wrap">
                    {['email', 'in_app', 'sms', 'webhook'].map(c => (
                      <label key={c} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox"
                          checked={impostazioni.canali_abilitati?.includes(c)}
                          onChange={e => setImpostazioni(p => ({
                            ...p,
                            canali_abilitati: e.target.checked
                              ? [...(p.canali_abilitati || []), c]
                              : (p.canali_abilitati || []).filter(x => x !== c)
                          }))}
                          className="rounded" />
                        <span className="text-sm text-white capitalize">{c}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-color mb-2 block">Priorità da Notificare</label>
                  <div className="space-y-2">
                    {[
                      { key: 'notifiche_critiche_immediate', label: 'Critiche (sempre immediate)', color: 'text-red-400' },
                      { key: 'notifiche_alta_priorita', label: 'Alta Priorità', color: 'text-orange-400' },
                      { key: 'notifiche_media_priorita', label: 'Media Priorità', color: 'text-yellow-400' },
                      { key: 'notifiche_bassa_priorita', label: 'Bassa Priorità', color: 'text-green-400' },
                    ].map(({ key, label, color }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox"
                          checked={impostazioni[key as keyof ImpostazioniNotifiche] as boolean}
                          onChange={e => setImpostazioni(p => ({ ...p, [key]: e.target.checked }))}
                          className="rounded" />
                        <span className={`text-sm font-semibold ${color}`}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={saveImpostazioni} disabled={saving}
                  className="px-6 py-2.5 bg-sky-500 text-white rounded-xl font-semibold text-sm hover:bg-sky-600 transition-colors disabled:opacity-50">
                  {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
